// Pantalla Control MDM (`/credit/mdm`): el único componente con estado del módulo. Ensambla la
// barra de KPIs, el explorer (filtros · lista · ficha) y el diálogo de acción.
import { useCallback, useMemo, useState } from 'react';
import type { LoanDeviceMdmStatusEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanDeviceMdmStatusEnum';
import { Loading } from '@/kit';
import { MasterDetailExplorer } from '@/shared/patterns/MasterDetail';
import { ScreenFrame } from '@/shared/patterns/ScreenFrame';
import {
    DEVICES_NOUN,
    LoadedScopeFooter,
    loadedCountsNote,
    loadedFiltersNote,
    type LoadedScope,
} from '@/shared/patterns/LoadedScope';
import { useAuth } from '@/hooks/useAuth';
import {
    isExecutableAction,
    useMdmDeviceAction,
    useMdmDeviceOperations,
    useMdmDeviceRefresh,
} from './api/useMdmFleet';
import { hasFleetDevices, useMdmFleetSource } from './api/mdmFleetSource';
import { creditIdOf, useMdmDeviceLookup } from './api/mdmDeviceLookup';
import { FLEET_STREAM_STATUSES, fleetReadFailureNote, imeiSearchOf } from './fleetStreams';
import type { MdmDeepLink } from './logic';
import {
    explainAmbiguousImei,
    explainDeepLinkNotFound,
    explainEmptyFleet,
    explainImeiSearchEmpty,
    explainLookupFailed,
    filterFleet,
    listBrands,
    listProviders,
    resolveDeepLink,
    summarizeFleet,
} from './logic';
import { type MdmAction } from './mdmActions';
import {
    announceMdmFailure,
    announceMdmOutcome,
    totalRejectionMessage,
    type MdmOutcomeContext,
} from './mdmOutcome';
import { mdmActionErrorMessage } from './actionErrors';
import { resolveMdmAbilities } from './permissions';
import {
    EMPTY_FLEET_FILTERS,
    type MdmActionRejection,
    type MdmFleetDevice,
    type MdmFleetFilters,
} from './types';
import { MdmPinResultDialog } from './components/MdmPinResultDialog';
import { MdmActionDialog, type MdmActionPayload } from './components/MdmActionDialog';
import type { MdmRefreshControl } from './components/MdmControlPanelTab';
import { MdmDeviceDetail, type MdmDetailTab } from './components/MdmDeviceDetail';
import { MdmFiltersPanel } from './components/MdmFiltersPanel';
import { MdmFleetKpis } from './components/MdmFleetKpis';
import {
    DEFAULT_FLEET_SORT,
    MdmFleetTable,
    sortDevices,
    type MdmFleetSortState,
} from './components/MdmFleetTable';
import { useMdmFleetPaginationTable } from './components/mdmFleetTableModel';
import { MdmHelpDialog } from './components/MdmHelpDialog';

/** La acción abierta en el diálogo. Siempre sobre UN equipo: no hay acciones en lote. */
interface OpenActionState {
    action: MdmAction;
    device: MdmFleetDevice;
}

/** Sin equipos leídos la tabla no pagina nada, pero el motor necesita un arreglo estable igual. */
const NO_DEVICES: MdmFleetDevice[] = [];

/** Ningún estado caído: referencia estable para cuando la flota todavía no llegó. */
const NO_FAILED_STATUSES: readonly LoanDeviceMdmStatusEnum[] = [];

function noLoadMore(): void {
    // Sin páginas pendientes el pie no pinta el botón; el motor igual pide una función.
}

/** El crédito que abre el deep link, o `null` si apuntó a un IMEI repartido en varios créditos. */
function deepLinkCreditId(link: MdmDeepLink): string | null {
    return link.kind === 'device' ? link.device.creditId : null;
}

const NO_DEEP_LINK: MdmDeepLink = { kind: 'none' };

/** Nota del filtro de búsqueda cuando va al servidor. */
const IMEI_SEARCH_NOTE =
    'Buscas un IMEI completo: lo buscamos en el servidor, en toda la flota, y los demás filtros no aplican.';

/** Los equipos de varias lecturas, sin repetir crédito; gana la primera lectura. */
function mergeByCredit(...lists: (readonly MdmFleetDevice[])[]): MdmFleetDevice[] {
    const seen = new Map<string, MdmFleetDevice>();
    for (const device of lists.flat()) {
        if (!seen.has(device.creditId)) seen.set(device.creditId, device);
    }
    return [...seen.values()];
}

export interface MdmPageProps {
    /** Deep link `?imei=`: abre la ficha de ese equipo. */
    imei?: string;
    /** Deep link `?credit=`: acepta la referencia legible (`CR-1188`) o el id del crédito. */
    credit?: string;
}

export function MdmPage({ imei, credit }: MdmPageProps) {
    const { permissions } = useAuth();
    const abilities = useMemo(() => resolveMdmAbilities(permissions), [permissions]);
    const [filters, setFilters] = useState<MdmFleetFilters>(EMPTY_FLEET_FILTERS);
    const [filtersCollapsed, setFiltersCollapsed] = useState(false);
    // Flota, búsqueda y deep link salen juntos en el primer render: ninguno espera al otro.
    const source = useMdmFleetSource(filters.state, abilities.canViewFleet);
    const searchImei = imeiSearchOf(filters.search);
    const search = useMdmDeviceLookup({ imei: searchImei }, abilities.canViewFleet);
    const link = useMdmDeviceLookup({ imei, credit }, abilities.canViewFleet);
    const ready = hasFleetDevices(source) ? source : null;
    const devices = ready?.devices ?? NO_DEVICES;
    const actionMutation = useMdmDeviceAction();
    const deviceRefresh = useMdmDeviceRefresh();

    // Instante de referencia del heartbeat, fijo mientras la pantalla está abierta: recalcularlo en
    // cada render rearmaría el orden de la lista bajo el cursor del operador.
    const now = useMemo<Date | string>(() => new Date(), []);

    const linkDevices = link.status === 'ready' ? link.devices : NO_DEVICES;
    // Se vuelve a cortar por IMEI: un backend que ignore `?imei=` devolvería la flota como resultado.
    const searchDevices = useMemo(
        () =>
            search.status === 'ready'
                ? search.devices.filter((device) => device.imei === searchImei)
                : NO_DEVICES,
        [search, searchImei],
    );
    const deepLink = useMemo(
        () => (link.status === 'ready' ? resolveDeepLink(linkDevices, imei, credit) : NO_DEEP_LINK),
        [link.status, linkDevices, imei, credit],
    );
    const hasDeepLink = link.status !== 'idle';
    // Con `?credit=` la bitácora no espera al equipo: el crédito ya se conoce desde la URL.
    const pendingLinkCreditId =
        link.status === 'loading' && !imei?.trim() ? (creditIdOf(credit) ?? null) : null;
    const knownDevices = useMemo(
        () => mergeByCredit(devices, linkDevices, searchDevices),
        [devices, linkDevices, searchDevices],
    );
    /** El crédito abierto en la ficha. `null` = todavía no eligieron fila. */
    const [selectedCreditId, setSelectedCreditId] = useState<string | null>(null);
    const [detailTab, setDetailTab] = useState<MdmDetailTab>('panel');
    const [sort, setSort] = useState<MdmFleetSortState>(DEFAULT_FLEET_SORT);
    const [openAction, setOpenAction] = useState<OpenActionState | null>(null);
    // El equipo tal como estaba al apretar «Actualizar»: de ahí sale el «antes» de cada fila, y su
    // crédito dice de quién es el veredicto que el hook sigue guardando.
    const [refreshBefore, setRefreshBefore] = useState<MdmFleetDevice | null>(null);
    // El passcode vive solo aquí y se pierde al cerrar: no se guarda ni se puede volver a pedir.
    const [pinResult, setPinResult] = useState<{ pin: string; validitySeconds?: number } | null>(
        null,
    );
    // El rechazo no se va con el toast: queda en la ficha de su equipo hasta que lo descarten.
    const [actionRejection, setActionRejection] = useState<MdmActionRejection | null>(null);

    // Con un `?imei=` repartido en varios créditos NO se abre ninguno: elegir uno sería decidir por
    // el operador. La lista se acota a las candidatas y el aviso de arriba dice por qué.
    const unresolvedImei =
        deepLink.kind === 'ambiguous' && selectedCreditId === null ? deepLink : null;
    const filteredDevices = useMemo(
        () => (searchImei ? searchDevices : filterFleet(devices, filters, now)),
        [searchImei, searchDevices, devices, filters, now],
    );
    // Un deep link sin coincidencia no abre la primera fila: sería mostrar el equipo de otro crédito.
    const missedDeepLink =
        deepLink.kind === 'notFound' && ready !== null && selectedCreditId === null
            ? deepLink
            : null;
    const visibleDevices = unresolvedImei ? unresolvedImei.devices : filteredDevices;
    const sortedDevices = useMemo(
        () => sortDevices(visibleDevices, sort, now),
        [visibleDevices, sort, now],
    );
    const brands = useMemo(() => listBrands(devices), [devices]);
    const providers = useMemo(() => listProviders(devices), [devices]);

    // Corta lo YA cargado en páginas; los controles del pie leen de aquí.
    const table = useMdmFleetPaginationTable(sortedDevices);
    const modelRows = table.getRowModel().rows;
    const pageDevices = useMemo(() => modelRows.map((row) => row.original), [modelRows]);

    // No nulo = quedan páginas por traer, así que lo cargado NO es la flota y la pantalla lo dice.
    const pending = ready !== null && ready.hasNextPage ? ready : null;
    // `null` = se pidieron todos los estados; si no, los KPIs de los demás no se pueden dar.
    const requestedStatuses =
        ready === null || ready.statuses.length === FLEET_STREAM_STATUSES.length
            ? null
            : ready.statuses;
    // Un estado que no contestó no es «faltan páginas»: falta entero, y el aviso lo nombra.
    const failedStatuses = ready?.failedStatuses ?? NO_FAILED_STATUSES;
    const readFailureNote = useMemo(() => fleetReadFailureNote(failedStatuses), [failedStatuses]);
    const wholeFleet =
        ready !== null &&
        requestedStatuses === null &&
        pending === null &&
        failedStatuses.length === 0;
    // Sin equipos leídos no hay universo del que hablar; con ellos, `hasMore` decide si va el rótulo.
    const scope: LoadedScope | null =
        ready === null ? null : { loaded: devices.length, hasMore: pending !== null };
    const countsNote = scope === null ? null : loadedCountsNote(scope, DEVICES_NOUN);
    const filtersNote = scope === null ? null : loadedFiltersNote(scope, DEVICES_NOUN);

    // Sin selección explícita abre en la primera fila de la página pintada.
    const fallbackCreditId = pageDevices.at(0)?.creditId ?? null;
    /**
     * El deep link NO se guarda en el estado: se deriva. Así se aplica solo cuando los equipos
     * llegan —no existen en el primer render— y lo que el operador elija después manda sobre él.
     */
    const openCreditId =
        selectedCreditId ??
        deepLinkCreditId(deepLink) ??
        pendingLinkCreditId ??
        (hasDeepLink ? null : fallbackCreditId);
    const selectedDevice = useMemo(
        () => knownDevices.find((device) => device.creditId === openCreditId) ?? null,
        [knownDevices, openCreditId],
    );
    // La bitácora se lee por crédito: sigue al equipo abierto, o al del deep link antes de que llegue.
    const auditLog = useMdmDeviceOperations(
        selectedDevice?.creditId ?? pendingLinkCreditId ?? undefined,
    );
    const fleetSummary = useMemo(() => summarizeFleet(devices, now), [devices, now]);

    let emptyMessage = explainEmptyFleet(fleetSummary, wholeFleet);
    if (searchImei && search.status === 'loading') emptyMessage = 'Buscando el equipo…';
    else if (searchImei && search.status === 'error')
        emptyMessage = explainLookupFailed(search.message);
    else if (searchImei) emptyMessage = explainImeiSearchEmpty(searchImei);

    const handleSelect = useCallback((nextCreditId: string) => {
        setSelectedCreditId(nextCreditId);
    }, []);

    // Cambiar el filtro cambia el universo: dejar la página en la 4 mostraría un hueco vacío.
    const handleFiltersChange = useCallback(
        (next: MdmFleetFilters) => {
            setFilters(next);
            table.setPageIndex(0);
        },
        [table],
    );

    // Ordenar reparte los equipos entre las páginas de otra manera: la 4 de la lista vieja no es
    // la 4 de la nueva, y el reordenamiento no le serviría de nada al operador.
    const handleSortChange = useCallback(
        (next: MdmFleetSortState) => {
            setSort(next);
            table.setPageIndex(0);
        },
        [table],
    );

    const { refresh: runRefresh } = deviceRefresh;
    const handleRefresh = useCallback(() => {
        if (!selectedDevice) return;
        setRefreshBefore(selectedDevice);
        void runRefresh(selectedDevice.creditId);
    }, [runRefresh, selectedDevice]);

    const handleSingleAction = useCallback(
        (action: MdmAction) => {
            if (!selectedDevice) return;
            // La consulta no es una orden: no abre diálogo y sale por su propio hook.
            if (action.key === 'refresh') {
                handleRefresh();
                return;
            }
            setOpenAction({ action, device: selectedDevice });
        },
        [handleRefresh, selectedDevice],
    );

    // El veredicto es de UN equipo: al abrir otra ficha no se arrastra el de la consulta anterior.
    const refreshedOpenDevice = refreshBefore?.creditId === selectedDevice?.creditId;
    const refreshControl: MdmRefreshControl = {
        onRefresh: handleRefresh,
        isPending: deviceRefresh.isPending,
        lastReview: refreshedOpenDevice ? deviceRefresh.lastReview : null,
        before: refreshedOpenDevice ? refreshBefore : null,
    };

    const closeActionDialog = useCallback(() => {
        setOpenAction(null);
    }, []);

    const dismissRejection = useCallback(() => {
        setActionRejection(null);
    }, []);

    // Ejecuta la acción confirmada, o explica por qué no se ejecutó: solo las que tienen endpoint
    // de backoffice publicado salen de aquí.
    const handleConfirmAction = useCallback(
        (payload: MdmActionPayload) => {
            if (!openAction) return;
            const { action, device: target } = openAction;
            // El diálogo de una acción sin endpoint no ofrece ejecutar: aquí solo llegan las que salen.
            if (!isExecutableAction(action.key)) {
                setOpenAction(null);
                return;
            }
            const context: MdmOutcomeContext = {
                key: action.key,
                label: action.label,
                scope: `IMEI ${target.imei}`,
                total: 1,
                provider: target.mdmProvider,
            };

            const reject = (message: string) => {
                setActionRejection({ creditId: target.creditId, label: action.label, message });
            };
            setActionRejection(null);
            actionMutation.mutate(
                {
                    key: action.key,
                    devices: [target],
                    newDueDate: payload.newDueDate,
                    comment: payload.comment,
                    devicePasskey: payload.devicePasskey,
                    notification: payload.notification,
                    release: payload.release,
                    outcomeContext: context,
                },
                {
                    onSuccess: (outcome) => {
                        const rejection = totalRejectionMessage(outcome);
                        if (rejection !== null) reject(rejection);
                        // El passcode va en su diálogo, no en un toast: el operador lo dicta y un
                        // aviso que se desvanece lo obliga a pedir otro, que invalida al anterior.
                        if (outcome.pin) {
                            setPinResult({
                                pin: outcome.pin,
                                validitySeconds: outcome.pinValiditySeconds,
                            });
                            return;
                        }
                        announceMdmOutcome(context, outcome);
                    },
                    onError: (mutationError) => {
                        reject(mdmActionErrorMessage(mutationError));
                        announceMdmFailure(context, mutationError);
                    },
                    // El diálogo cierra cuando la acción termina, no al apretar: sin feedback el
                    // operador reintenta.
                    onSettled: () => {
                        setOpenAction(null);
                    },
                },
            );
        },
        [actionMutation, openAction],
    );

    if (!abilities.canViewFleet) {
        return (
            <div role="alert" className="p-6 text-[12.5px] text-muted-foreground">
                No tienes permiso para ver el fleet MDM. Si crees que es un error, contacta al
                administrador.
            </div>
        );
    }

    if (source.status === 'loading') {
        return <Loading fullScreen text="Cargando equipos…" />;
    }

    // Solo vacía la pantalla el fallo SIN nada cargado; el de una página posterior lo dice el pie.
    if (source.status === 'error') {
        return (
            <div role="alert" className="p-6 text-[12.5px] text-destructive">
                No se pudo cargar el inventario de equipos. {source.message}
            </div>
        );
    }

    return (
        <ScreenFrame>
            <div className="flex flex-none items-center justify-end border-b border-border bg-card py-1">
                <MdmHelpDialog />
            </div>

            <MdmFleetKpis
                summary={fleetSummary}
                scope={scope}
                statuses={requestedStatuses}
                failedStatuses={failedStatuses}
            />

            {readFailureNote === null ? null : (
                <div
                    role="alert"
                    className="flex-none border-b border-destructive/40 bg-destructive/10 px-3 py-2 text-[11.5px] leading-snug text-foreground"
                >
                    {readFailureNote}
                </div>
            )}

            {unresolvedImei ? (
                <div
                    role="status"
                    className="flex-none border-b border-warning/40 bg-warning/10 px-3 py-2 text-[11.5px] text-foreground"
                >
                    {explainAmbiguousImei(unresolvedImei.imei, unresolvedImei.devices.length)}
                </div>
            ) : null}

            {link.status === 'error' && selectedCreditId === null ? (
                <div
                    role="alert"
                    className="flex-none border-b border-destructive/40 bg-destructive/10 px-3 py-2 text-[11.5px] text-foreground"
                >
                    {explainLookupFailed(link.message)}
                </div>
            ) : null}

            {missedDeepLink ? (
                <div
                    role="status"
                    className="flex-none border-b border-warning/40 bg-warning/10 px-3 py-2 text-[11.5px] text-foreground"
                >
                    {explainDeepLinkNotFound(missedDeepLink)}
                </div>
            ) : null}

            {countsNote === null ? null : (
                <div
                    role="status"
                    className="flex-none border-b border-warning/40 bg-warning/10 px-3 py-2 text-[11.5px] leading-snug text-foreground"
                >
                    {countsNote}
                </div>
            )}

            <MasterDetailExplorer
                filtersCollapsed={filtersCollapsed}
                filters={
                    <MdmFiltersPanel
                        filters={filters}
                        onChange={handleFiltersChange}
                        brands={brands}
                        providers={providers}
                        loadedNote={searchImei ? null : filtersNote}
                        searchNote={searchImei ? IMEI_SEARCH_NOTE : null}
                        collapsed={filtersCollapsed}
                        onToggleCollapsed={() => {
                            setFiltersCollapsed((prev) => !prev);
                        }}
                    />
                }
                list={
                    <>
                        <MdmFleetTable
                            devices={pageDevices}
                            sort={sort}
                            onSortChange={handleSortChange}
                            emptyMessage={emptyMessage}
                            now={now}
                            selectedCreditId={openCreditId}
                            onSelect={handleSelect}
                        />
                        <LoadedScopeFooter
                            noun={DEVICES_NOUN}
                            scope={scope}
                            shown={visibleDevices.length}
                            table={table}
                            hasNextPage={pending !== null}
                            isFetchingNextPage={ready?.isFetchingNextPage ?? false}
                            onLoadMore={ready?.fetchNextPage ?? noLoadMore}
                            nextPageError={ready?.nextPageError ?? null}
                        />
                    </>
                }
                detail={
                    <MdmDeviceDetail
                        device={selectedDevice}
                        abilities={abilities}
                        now={now}
                        auditEntries={auditLog.entries}
                        auditIsLoading={auditLog.isLoading}
                        auditError={auditLog.error}
                        tab={detailTab}
                        onTabChange={setDetailTab}
                        refresh={refreshControl}
                        onAction={handleSingleAction}
                        actionRejection={
                            actionRejection?.creditId === selectedDevice?.creditId
                                ? actionRejection
                                : null
                        }
                        onDismissRejection={dismissRejection}
                    />
                }
            />

            {openAction ? (
                <MdmActionDialog
                    action={openAction.action}
                    device={openAction.device}
                    isPending={actionMutation.isPending}
                    onCancel={closeActionDialog}
                    onConfirm={handleConfirmAction}
                />
            ) : null}

            <MdmPinResultDialog
                pin={pinResult?.pin}
                validitySeconds={pinResult?.validitySeconds}
                onClose={() => {
                    setPinResult(null);
                }}
            />
        </ScreenFrame>
    );
}
